<template>
  <div class="form-group">
    <label
      v-if="$props.label"
      :for="componentId"
      :class="['form-label', { 'form-label--sr-only': $props.labelSrOnly }]"
      :aria-label="$props.label"
    >
      {{ $props.label }}
      <span
        v-if="!$props.required"
        class="optional-label"
      >{{ $props.optionalLabel }}</span>
      <span v-if="$props.required && $props.label" class="required"></span>
    </label>
    <div :class="[
      value.disabled ? `form-input form-input-disabled ${vanityClassName}` : `form-input ${vanityClassName}`,
      error && `form-input--error`,
    ]"
    >
      <select
        :id="componentId"
        ref="inputEl"
        class="form-input__field"
        v-bind="$attrs"
        @blur="onBlur()"
        @change="change"
      >
        <template v-for="(option, index) in options">
          <optgroup
            v-if="option.options && option.options.length > 0"
            :key="`${option.value}-${option.label}-${index}`"
            :label="option.label"
          >
            <option
              v-for="(innerOption, idx) in option.options"
              :key="`${innerOption.value}-${idx}`"
              :value="innerOption.value"
              :disabled="innerOption.disabled"
              :selected="selectedOption(innerOption)"
            >{{ innerOption.label }}</option>
          </optgroup>
          <option
            v-else
            :key="`${option.value}-${option.label}-${index}`"
            :value="option.value"
            :disabled="option.disabled"
            :selected="selectedOption(option)"
          >{{ option.label }}</option>
        </template>
      </select>
      <icon name="chevron-down" class="form-input__icon form-input__icon--select" />
    </div>
    <p
      v-if="$props.error"
      class="form-label form-label--error form-label--error--active"
    >{{ $props.errorMessage }}</p>

    <div v-if="$slots.default" class="form-help">
      <!-- @slot additional information wrapper in a `form-help` class -->
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { Icon } from "../../components";

export default {
  name: "FormSelect",
  components: { Icon },
  inheritAttrs: false,
  props: {
    /**
     * @description
     * Label for the dropdown
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Should the label be visually hidden? It will still be read by screen readers.
     */
    labelSrOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * The text displayed in the label when the input is not required
     */
    optionalLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Array of objects of format `{ value: String, label: String, disabled: Boolean }` that will be rendered in the dropdown
     * To do option groups, the format should be `{ label: String, options: []}`
     */
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    value: {
      type: Object,
      default() {
        return {};
      },
    },
    id: {
      type: String,
      required: true,
    },
    /**
     * @description
     * Flag to show if the form input is in an error state
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * The message displayed when the input is in an error state
     */
    errorMessage: {
      type: String,
      default: "",
    },
    vanityStyle: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      selected: null,
      localValue: this.value,
    };
  },
  computed: {
    componentId() {
      return this.$props.id || this.$attrs.name;
    },
    vanityClassName() {
      return this.vanityStyle ? "form-input--vanity" : "";
    },
  },
  watch: {
    value() {
      this.localValue = this.value;
    },
  },
  methods: {
    selectedOption(option) {
      // if (option.disabled) {
      //   return true;
      // }
      if (this.localValue && this.localValue.value) {
        return option.value === this.localValue.value;
      }
      return false;
    },
    change(e) {
      if (!(e && this.options && this.options.length > 0)) {
        return;
      }
      const selectedItem = e.target.value;
      let selectedOption;
      // Loop through the layers and get the first match for the selected item.
      for (const option of this.options) {
        if (option.options && Array.isArray(option.options)) {
          selectedOption = option.options.find(
            (innerOption) => selectedItem == innerOption.value
          );
          if (selectedOption) {
            // selectedOption = io;
            break;
          }
        } else {
          if (selectedItem == option.value) {
            selectedOption = option;
            break;
          }
        }
      }
      this.localValue = selectedOption;
      this.$emit("input", this.localValue);
    },
    focus() {
      this.$refs.inputEl.focus();
    },
    onBlur() {
      setTimeout(() => {
        this.$emit("blur");
      }, 100);
    },
  },
};
</script>

<style lang="scss" >
@import "../../styles/imports";
  .form-input-disabled {
    background-color: #f8f8f8 !important;
  }
  .required:after {
    content:" *";
    color: red;
  }
</style>